import type { ExportRenderQualityId } from '@/rendering/export/types'

/**
 * 用户可选的视频导出容器格式。
 * `mp4` 走 WebCodecs H.264 + mp4-muxer；`webm` 走 WebCodecs VP9/VP8 + webm-muxer（或 MediaRecorder 回退）。
 */
export type VideoExportFormatId = 'mp4' | 'webm'

/** 用户可选的导出帧率。 */
export type VideoExportFps = 30 | 60

/** UI 下拉候选帧率（与 {@link VideoExportFps} 一致，供组件迭代）。 */
export const VIDEO_EXPORT_FPS_OPTIONS: readonly VideoExportFps[] = [30, 60]

/** UI 下拉候选格式（顺序即展示顺序；第一个为默认）。 */
export const VIDEO_EXPORT_FORMAT_OPTIONS: readonly VideoExportFormatId[] = ['mp4', 'webm']

/** 默认导出帧率：60fps，顺滑优先。 */
export const DEFAULT_VIDEO_EXPORT_FPS: VideoExportFps = 60

/** 默认导出格式：MP4（H.264），兼容性与分享性优先。 */
export const DEFAULT_VIDEO_EXPORT_FORMAT: VideoExportFormatId = 'mp4'

/**
 * 内部统一的渲染/编码质量档（不对外暴露）。
 *
 * `best` 在场景渲染阶段启用最高抗锯齿与阴影，在 WebCodecs 阶段按参考码率 × 分辨率缩放给出更高目标码率。
 * 详见 {@link computeExportVideoBitrateBps}。
 */
export const INTERNAL_EXPORT_QUALITY: ExportRenderQualityId = 'best'

/**
 * 关键帧（GOP）间隔秒数：2 秒一个 IDR，适配 Web 播放随机访问。
 */
export const KEYFRAME_INTERVAL_SECONDS = 2

/**
 * 计算关键帧间帧数：至少 30，约每 2 秒一次。
 *
 * @param fps - 正整数帧率
 */
export function computeKeyframeInterval(fps: number): number {
  const f = Math.max(1, Math.round(Number(fps) || 0))
  return Math.max(30, Math.round(f * KEYFRAME_INTERVAL_SECONDS))
}

/**
 * 按导出容器推导下载用的 MIME 与文件扩展名。
 *
 * @param format - 视频导出容器
 */
export function videoContainerMeta(format: VideoExportFormatId): {
  mime: string
  extension: 'mp4' | 'webm'
} {
  if (format === 'mp4') return { mime: 'video/mp4', extension: 'mp4' }
  return { mime: 'video/webm', extension: 'webm' }
}
